import { Autocomplete, FormControl, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { makeFormStyles } from 'forms/styles';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import {
    getAssetsPicklist,
    selectAssetsPicklist,
    selectAssetsPicklistLoadingStatus,
} from 'features/picklists/pickListsSlice';
import { setProcedureDirty } from 'features/procedures/proceduresSlice';
import { assetService } from 'features/services';
import { LoadingLogo } from 'GeminiViewerComponent/components/LoadingLogo/LoadingLogo';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { AssetTypes } from 'GeminiViewerComponent/helpers';
import { LoadingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { } from 'helpers';
import { MdDelete } from 'react-icons/md';
import { CommonNodeFormFields, ExcludeFromNavigationField, NodeCategoryField } from '../CommonNodeFormFields';
import DirtyProcedure from '../DirtyProcedure';
import SaveNodeDataDialog from '../SaveNodeDataDialog';

const makeNodeFormStyles = makeStyles(() => ({
    container: {
        padding: '20px',
        width: '100%',
        height: '100%',
    },
    form: {
        display: 'flex',
        width: '100%',
        height: 'auto',
        padding: '10px',
        border: '1px solid',
    },
    formBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        width: '100%',
    },
    submitButtonRow: {
        marginTop: 'auto',
        alignSelf: 'end',
        display: 'flex',
        gap: '5px',
    },
    contentRow: {
        cursor: 'pointer',
    },
    dialogPaper: {
        minHeight: '100%',
        maxHeight: '100%',
    },
    addButtonIcon: {
        padding: '3px !important',
        borderRadius: '25px !important',
        background: (props) => `${props.colors.button.primaryBackground} !important`,
        color: (props) => `${props.colors.button.primaryForeground} !important`,
    },
}));

const BranchNodeForm = ({
    nodes,
    node,
    onCancel,
    onSubmit,
    showTwoPanelMode,
    assetId,
    openSaveDataDialog,
    setOpenSaveDataDialog,
    procedureCategories,
    isNewNode = false,
}) => {
    const theme = useSelector(selectDefaultTheme);
    const nodeFormStyles = makeNodeFormStyles(theme);
    const commonFormStyles = makeFormStyles(theme);
    const dispatch = useDispatch();
    const assetListStatus = useSelector(selectAssetsPicklistLoadingStatus);

    const assetPicklist = useSelector(selectAssetsPicklist);
    const assetList = useMemo(() => {
        return assetPicklist.filter(
            (asset) => asset.asset_type_id === AssetTypes.Procedure && asset.asset_id !== assetId
        );
    }, [assetId, assetPicklist]);

    const optionReturnNodes = [{ id: 0 }, ...nodes];

    const [formValues, setFormValues] = useState({
        id: 0,
        title: '',
        node_category: null,
        exclude_from_navigation: false,
        asset_id: null,
        start_node_id: 0,
        return_node_id: 0,
        ...node,
    });
    const [branchAssetNodes, setBranchAssetNodes] = useState([]);

    useEffect(() => {
        setFormValues({
            ...node,
            procedureCategories,
            exclude_from_navigation: node?.exclude_from_navigation ?? false,
            asset_id: node?.asset_id ?? null,
            start_node_id: node?.start_node_id ?? 0,
            return_node_id: node?.return_node_id ?? 0,
        });
    }, [node, procedureCategories]);

    useEffect(() => {
        return () => {
            setFormValues({});
            dispatch(setProcedureDirty(false));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (assetListStatus === LoadingStatus.Idle) {
            dispatch(getAssetsPicklist());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        async function getBranchNodes(assetId) {
            if (assetId) {
                const response = await assetService.getById(assetId);
                if (!response.error) {
                    setBranchAssetNodes([{ id: 0 }, ...(JSON.parse(response?.procedure_json)?.nodes ?? [])]);
                }
            }
        }
        if (assetListStatus === LoadingStatus.Loaded && node?.asset_id) {
            getBranchNodes(node.asset_id);
        }
    }, [node?.asset_id, assetListStatus]);

    const handleAssetChange = async (assetId, setFieldValue) => {
        let isAssetSelected = false;
        setFieldValue('asset_id', assetId ?? null);
        setFieldValue('start_node_id', 0);
        if (assetId) {
            const response = await assetService.getById(assetId);
            if (!response.error) {
                isAssetSelected = true;
                setBranchAssetNodes([{ id: 0 }, ...(JSON.parse(response?.procedure_json)?.nodes ?? [])]);
            }
        }
        if (!isAssetSelected) {
            setBranchAssetNodes([]);
        }
    };

    if (assetListStatus === LoadingStatus.Loading) {
        return <LoadingLogo styleClassName={commonFormStyles.loadingSvg} />;
    }

    return (
        <div className={nodeFormStyles.container}>
            <div className={nodeFormStyles.form}>
                <Formik
                    key={`${node.id}-${node?.isNew ? 'new' : 'saved'}`}
                    enableReinitialize={true}
                    initialValues={formValues}
                    validationSchema={Yup.object({
                        title: Yup.string().required('Required'),
                        asset_id: Yup.number().nullable().min(0).required('Required'),
                        start_node_id: Yup.number().min(0).required('Required'),
                        return_node_id: Yup.number().min(0).required('Required'),
                    })}
                    onSubmit={async (values) => {
                        onSubmit(values);
                    }}
                >
                    {({ values, errors, touched, isSubmitting, isValid, dirty, resetForm, setFieldValue }) => (
                        <Form className={nodeFormStyles.formBody}>
                            <DirtyProcedure nodeId={node.id} isNewNode={isNewNode} />
                            <CommonNodeFormFields />
                            <NodeCategoryField />
                            <ExcludeFromNavigationField />
                            <FormControl fullWidth margin="dense">
                                <Field name="asset_id">
                                    {({ field }) => (
                                        <Autocomplete
                                            {...field}
                                            variant="outline"
                                            id="asset_id"
                                            value={
                                                assetList?.find((asset) => asset.asset_id === values.asset_id) ?? null
                                            }
                                            options={assetList}
                                            getOptionLabel={(option) => option.display_name}
                                            clearIcon={<MdDelete fontSize="18" />}
                                            onChange={(_, newInputValue) => {
                                                handleAssetChange(newInputValue?.asset_id, setFieldValue);
                                            }}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.asset_id}>
                                                    {option.display_name}
                                                </li>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={errors.asset_id && touched.asset_id}
                                                    variant="outlined"
                                                    label="Procedure *"
                                                    placeholder="Procedure"
                                                />
                                            )}
                                        />
                                    )}
                                </Field>
                                <ErrorMessage className={commonFormStyles.inputErr} name="asset_id" component="div" />
                            </FormControl>
                            {values?.asset_id > 0 && (
                                <FormControl fullWidth margin="dense">
                                    <Field name="start_node_id">
                                        {({ field }) => (
                                            <Autocomplete
                                                {...field}
                                                getOptionLabel={(option) =>
                                                    option.id === 0
                                                        ? 'Default'
                                                        : option.title
                                                        ? `${option.title} - #${option.id}`
                                                        : `${option.type} - #${option.id}`
                                                }
                                                variant="outline"
                                                id="start_node_id"
                                                options={branchAssetNodes}
                                                value={
                                                    branchAssetNodes?.find(
                                                        (node) => Number(node?.id) === Number(field.value)
                                                    ) ?? null
                                                }
                                                clearIcon={<MdDelete fontSize="18" />}
                                                onChange={(_, newInputValue) => {
                                                    setFieldValue('start_node_id', newInputValue?.id ?? 0);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={errors.start_node_id && touched.start_node_id}
                                                        variant="outlined"
                                                        label="Start Node *"
                                                        placeholder="Start Node"
                                                    />
                                                )}
                                            />
                                        )}
                                    </Field>
                                    <ErrorMessage
                                        className={commonFormStyles.inputErr}
                                        name="start_node_id"
                                        component="div"
                                    />
                                </FormControl>
                            )}
                            <FormControl fullWidth margin="dense">
                                <Field name="return_node_id">
                                    {({ field }) => (
                                        <Autocomplete
                                            {...field}
                                            getOptionLabel={(option) =>
                                                option.id === 0
                                                    ? 'Default'
                                                    : option.title
                                                    ? `${option.title} - #${option.id}`
                                                    : `${option.type} - #${option.id}`
                                            }
                                            variant="outline"
                                            id="return_node_id"
                                            options={optionReturnNodes}
                                            value={
                                                optionReturnNodes.find(
                                                    (node) => Number(node?.id) === Number(field.value)
                                                ) ?? null
                                            }
                                            clearIcon={<MdDelete fontSize="18" />}
                                            onChange={(_, newInputValue) => {
                                                setFieldValue(
                                                    'return_node_id',
                                                    newInputValue?.id ? newInputValue?.id : 0
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={errors.return_node_id && touched.return_node_id}
                                                    variant="outlined"
                                                    label="Return Node *"
                                                    placeholder="Return Node"
                                                />
                                            )}
                                        />
                                    )}
                                </Field>
                                <ErrorMessage
                                    className={commonFormStyles.inputErr}
                                    name="return_node_id"
                                    component="div"
                                />
                            </FormControl>

                            {openSaveDataDialog && (
                                <SaveNodeDataDialog
                                    openSaveDataDialog={openSaveDataDialog}
                                    setOpenSaveDataDialog={setOpenSaveDataDialog}
                                    isSubmitting={isSubmitting}
                                    onCancel={() => {
                                        if (!node?.isNew) {
                                            resetForm();
                                        }
                                        onCancel();
                                    }}
                                />
                            )}
                            <div className={nodeFormStyles.submitButtonRow}>
                                <button
                                    className={commonFormStyles.cancel}
                                    type="button"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        if (dirty) {
                                            setOpenSaveDataDialog(true);
                                        } else {
                                            if (!node?.isNew) {
                                                resetForm();
                                            }
                                            onCancel();
                                        }
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={commonFormStyles.submit}
                                    type="submit"
                                    disabled={isSubmitting || !isValid}
                                >
                                    Submit
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};
export default BranchNodeForm;
